import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { ScaleLeadTimeDistributionModal } from './ScaleLeadTimeDistributionModal.js';
import { TabButton, TabGroup } from '../../ui/Tabs/index.js';
import { PMO_SCALE_ALL_MONTHS_KEY } from '../../../types/pmo-flow.js';
import type { CSSProperties } from 'react';
import type {
  PmoScaleExcludeMode,
  PmoScaleLeadTimeResponse,
  PmoScaleScopeKey,
  PmoScaleScopeStat,
} from '../../../types/pmo-flow.js';

/**
 * 規模帯の表示順。API も同順で返すが、UI 側でも順序を固定しておく
 * （並びが規模の大小そのものなので、レスポンス順に依存させない）。
 * @graph-connects none
 */
const BAND_ORDER = ['pt1To50', 'pt51To100', 'ptOver100'] as const;

/**
 * 遅い側の除外モードの並び（トグルの左から右）。
 * 「そのまま → p75 以上を外す → p90 以上を外す」の順で、右へ行くほど尾を強く削る。
 * @graph-connects none
 */
const EXCLUDE_MODES: readonly PmoScaleExcludeMode[] = ['none', 'p75', 'p90'] as const;

/**
 * サンプル数がこの件数を下回る帯は参考値として控えめに見せる。
 * フェーズリードタイム側の `sparse` 閾値と同じ値に揃える。
 * @graph-connects none
 */
const SPARSE_COUNT_THRESHOLD = 30;

/**
 * 帯ごとの色。規模が上がるほど濃くする（同系色で大小を読ませる）。
 * 全体だけは中立のスレートにして、規模の系列とは別物（3 帯を混ぜた基準線）だと分かるようにする
 * @graph-connects none
 */
const SCOPE_COLORS: Record<PmoScaleScopeKey, string> = {
  overall: 'var(--color-pmo-phase-mixed)',
  pt1To50: 'var(--color-pmo-phase-work)',
  pt51To100: 'var(--color-pmo-phase-work-strong)',
  ptOver100: 'var(--color-pmo-phase-project-total)',
};

export interface ScaleLeadTimeCardProps {
  /** 規模別リードタイム（未取得なら undefined） */
  data: PmoScaleLeadTimeResponse | undefined;
  /** 読み込み状態 */
  isLoading: boolean;
  /** 選択中のリリース完了月。未選択（null）ならサーバー既定の最新月に従う */
  selectedMonth: string | null;
  /** リリース完了月の切り替え */
  onSelectMonth: (month: string) => void;
  /** 遅い側の除外モード */
  excludeMode: PmoScaleExcludeMode;
  /** 除外モードの切り替え */
  onSelectExcludeMode: (mode: PmoScaleExcludeMode) => void;
}

/**
 * 遅い側の除外モードを切り替える 3 択トグル。
 *
 * セレクトではなくトグルにするのは、この操作が「絞り込み」ではなく**同じ母集団の見え方の
 * 切り替え**で、3 つを行き来して差そのものを読む使い方になるため。ピル型セグメント
 * スイッチはアプリ規約どおり `TabGroup` + `TabButton` を使い、選択中の位置は
 * `TabGroup` 既定のスライドピルで示す（同じディレクトリの `PhaseCompositionCard` と同方式）。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 案件規模別リードタイムの遅い側除外モード（そのまま / p75以上を除外 / p90以上を除外）を切り替える TabGroup スイッチを描画する
 * @graph-connects TabGroup [calls] 除外モードのピル型スイッチ描画
 * @graph-connects TabButton [calls] 除外モード各選択肢のボタン描画
 */
function ExcludeModeToggle({
  value,
  onChange,
}: {
  value: PmoScaleExcludeMode;
  onChange: (mode: PmoScaleExcludeMode) => void;
}) {
  const { t } = useTranslation();

  return (
    <TabGroup ariaLabel={t('kpi.pmoFlow.scaleLeadTime.exclude.label')}>
      {EXCLUDE_MODES.map((mode) => (
        <TabButton
          key={mode}
          active={mode === value}
          label={t(`kpi.pmoFlow.scaleLeadTime.exclude.${mode}`)}
          onClick={() => {
            onChange(mode);
          }}
        />
      ))}
    </TabGroup>
  );
}

/**
 * `YYYY-MM` を表示用の月ラベルへ直す。まとめ表示のキーは月ではないので呼ばない。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件規模別リードタイムの月セレクト用に YYYY-MM を年月ラベルへ変換する
 * @graph-connects none
 */
export function parseScaleMonth(monthKey: string): { year: number; month: number } | null {
  const matched = /^(\d{4})-(\d{2})$/.exec(monthKey);
  if (matched === null) {
    return null;
  }
  return { year: Number(matched[1]), month: Number(matched[2]) };
}

/**
 * 全ブロックで共通のスケール上限。帯ごとに軸を変えると「100pt 超の方が短い」と
 * 読める絵が出来てしまうため、全体と全帯の p90 の最大値を 1 本の軸として共有する。
 * どれも 0 件なら軸が引けないので null。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件規模別リードタイムの全ブロック共通スケール上限（全体と各帯のp90の最大値）を求める
 * @graph-connects none
 */
export function scaleAxisMax(scopes: readonly PmoScaleScopeStat[]): number | null {
  const p90s = scopes
    .map((scope) => scope.days?.p90)
    .filter((value): value is number => value !== undefined);
  if (p90s.length === 0) {
    return null;
  }
  const max = Math.max(...p90s);
  // 全帯が 0 日（同日リリース）だと幅が計算できないので軸を引かない
  return max > 0 ? max : null;
}

/**
 * ブロック 1 つ分（規模帯 or 全体）。件数・平均実績pt・p50・p90 と、共通スケール上の帯グラフを描く。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件規模帯 or 全体 1件分のリードタイム（件数・平均実績pt・p50・p90・共通スケール上のバー）を、分位分布ドリルダウンを開くボタンとして描画する
 * @graph-connects none
 */
function ScaleScopeBlock({
  scope,
  axisMax,
  emphasis = false,
  onOpenDistribution,
}: {
  scope: PmoScaleScopeStat;
  axisMax: number | null;
  /** 全体ブロックは基準線なので、数値を一段大きくして 3 帯より前に立たせる */
  emphasis?: boolean;
  onOpenDistribution: (key: PmoScaleScopeKey) => void;
}) {
  const { t } = useTranslation();
  const color = SCOPE_COLORS[scope.key];
  const isSparse = scope.count > 0 && scope.count < SPARSE_COUNT_THRESHOLD;

  const trackStyle: CSSProperties = {
    backgroundColor: `color-mix(in srgb, ${color} 20%, transparent)`,
  };
  // p50 までを塗り、p90 の位置にマーカーを立てる。塗りが左に寄っているほど
  // 「たいていは短いが、たまに極端に伸びる」形であることが読める
  const fillStyle: CSSProperties =
    scope.days && axisMax !== null
      ? { backgroundColor: color, width: `${String((scope.days.p50 / axisMax) * 100)}%` }
      : { width: '0%' };
  // マーカーはトラック（overflow-hidden）の外に置き、中心を p90 の位置に合わせる。
  // トラックの内側に入れると、軸の最大値を決めている帯（p90 = axisMax）で
  // left が 100% になり、まるごとクリップされて縦線が消える。
  // 色も塗りと変える。同色だと塗りの一部に見えて「何の線か」が読めない
  const markerStyle: CSSProperties =
    scope.days && axisMax !== null
      ? {
          left: `${String((scope.days.p90 / axisMax) * 100)}%`,
          transform: 'translateX(-50%)',
        }
      : { display: 'none' };

  // 分布を持たない帯（0 件）は開いても見せるものが無いので押せなくする
  const canOpen = scope.days !== null && scope.distribution.length > 0;

  return (
    <button
      type="button"
      disabled={!canOpen}
      onClick={() => {
        onOpenDistribution(scope.key);
      }}
      className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4 text-left transition-colors enabled:cursor-pointer enabled:hover:border-[var(--color-border-hover)] enabled:hover:bg-[var(--color-bg-hover)] disabled:cursor-default"
      data-testid={`pmo-scale-band-${scope.key}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <span className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t(`kpi.pmoFlow.scaleLeadTime.band.${scope.key}`)}
        </span>
        <span className="flex flex-wrap items-center gap-1.5">
          {isSparse && (
            <Badge variant="warning" size="sm">
              {t('kpi.pmoFlow.badge.sparse')}
            </Badge>
          )}
          <span className="rounded-full bg-[var(--color-bg-hover)] px-2 py-0.5 text-[11px] font-medium tabular-nums text-[var(--color-text-secondary)]">
            {t('kpi.pmoFlow.scaleLeadTime.count', { count: scope.count })}
          </span>
          {/* 帯は幅を持つので、日数がどれくらいの規模に対する値なのかを併せて出す */}
          {scope.averagePoint !== null && (
            <span
              className="rounded-full bg-[var(--color-bg-hover)] px-2 py-0.5 text-[11px] font-medium tabular-nums text-[var(--color-text-secondary)]"
              title={t('kpi.pmoFlow.scaleLeadTime.averagePointHint')}
            >
              {t('kpi.pmoFlow.scaleLeadTime.averagePoint', { value: scope.averagePoint })}
            </span>
          )}
        </span>
      </div>

      {scope.days ? (
        <>
          <div className="mt-3 flex items-end gap-4">
            <span className="flex flex-col">
              <span className="text-[11px] text-[var(--color-text-muted)]">
                {t('kpi.pmoFlow.scaleLeadTime.p50')}
              </span>
              <span
                className={`font-mono font-bold tabular-nums text-[var(--color-text-primary)] ${emphasis ? 'text-3xl' : 'text-2xl'}`}
              >
                {scope.days.p50}
                <span className="ml-0.5 text-xs font-normal text-[var(--color-text-muted)]">
                  {t('kpi.pmoFlow.unit.daysSuffix')}
                </span>
              </span>
            </span>
            <span className="flex flex-col">
              <span
                className="text-[11px] text-[var(--color-text-muted)]"
                title={t('kpi.pmoFlow.scaleLeadTime.p90Hint')}
              >
                {t('kpi.pmoFlow.scaleLeadTime.p90')}
              </span>
              <span className="font-mono text-lg font-semibold tabular-nums text-[var(--color-text-secondary)]">
                {scope.days.p90}
                <span className="ml-0.5 text-xs font-normal text-[var(--color-text-muted)]">
                  {t('kpi.pmoFlow.unit.daysSuffix')}
                </span>
              </span>
            </span>
          </div>

          <div
            className="relative mt-3"
            role="img"
            aria-label={t('kpi.pmoFlow.scaleLeadTime.barLabel', {
              p50: scope.days.p50,
              p90: scope.days.p90,
            })}
          >
            <div className="h-2 w-full overflow-hidden rounded-full" style={trackStyle}>
              <div className="h-full rounded-full" style={fillStyle} />
            </div>
            <span
              aria-hidden="true"
              className="absolute -top-0.5 h-3 w-0.5 rounded-full bg-[var(--color-text-primary)]"
              style={markerStyle}
              data-testid={`pmo-scale-marker-${scope.key}`}
            />
            <span
              aria-hidden="true"
              className="absolute top-3.5 text-[10px] leading-none tabular-nums text-[var(--color-text-muted)]"
              style={markerStyle}
            >
              {t('kpi.pmoFlow.scaleLeadTime.p90')}
            </span>
          </div>
        </>
      ) : (
        <p className="mt-3 text-xs text-[var(--color-text-muted)]">
          {t('kpi.pmoFlow.scaleLeadTime.noSample')}
        </p>
      )}

      {canOpen && (
        <p className="mt-5 text-[11px] text-[var(--color-text-muted)]">
          {t('kpi.pmoFlow.scaleLeadTime.openDistribution')}
        </p>
      )}
    </button>
  );
}

/**
 * 案件規模別リードタイムカード。**リリース完了月 1 ヶ月**の母票を実績 pt の規模帯に分け、
 * 開始日からリリース完了日までの営業日を p50 / p90 で並べる。
 *
 * フェーズ構成カードが「案件の中でどこに時間が溶けているか」を見るのに対し、こちらは
 * 工程を割らずに「その規模の案件は結局どれだけ掛かるか」を見る。見積の妥当性を測る
 * 参照値になるので、3 ブロックは同じ軸で描いて規模間の比較ができる形にする。
 *
 * 半年をまとめると「今どうなっているか」と「半年前どうだったか」が 1 本の分位に潰れる。
 * 月ごとに切って並べ替えられるようにし、母数が要るときだけまとめ表示へ退避する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO案件規模別リードタイムカード。選択したリリース完了月について、規模を問わない全体と実績pt帯（1〜50/51〜100/100pt超）ごとに開始日→リリース完了日のp50・p90・件数・平均実績ptを共通スケールで表示し、月セレクトと遅い側の除外トグルで切り替える
 * @graph-connects ScaleScopeBlock [calls] 全体・規模帯1件分のブロック描画
 * @graph-connects ExcludeModeToggle [calls] 遅い側の除外モード切り替えトグルの描画
 * @graph-connects ScaleLeadTimeDistributionModal [calls] 分位分布ドリルダウンの表示
 * @graph-connects scaleAxisMax [calls] 共通スケール上限の算出
 * @graph-connects parseScaleMonth [calls] 月キーの表示ラベル変換
 * @graph-connects Card [calls] カードコンポーネント描画
 * @graph-connects Badge [calls] 対象件数バッジ描画
 * @graph-connects Select [calls] リリース完了月セレクト描画
 */
export function ScaleLeadTimeCard({
  data,
  isLoading,
  selectedMonth,
  onSelectMonth,
  excludeMode,
  onSelectExcludeMode,
}: ScaleLeadTimeCardProps) {
  const { t } = useTranslation();
  const [openedScopeKey, setOpenedScopeKey] = useState<PmoScaleScopeKey | null>(null);

  // セレクトが指すのは「実際に集計された月」。要求した月をそのまま出すと、
  // サーバーが窓の外の月を最新月へ落としたときに見出しと中身がずれる
  const activeMonth = data?.month ?? selectedMonth ?? '';
  const monthOptions = [
    ...(data?.availableMonths ?? []).map((monthKey) => {
      const parsed = parseScaleMonth(monthKey);
      return {
        value: monthKey,
        label:
          parsed === null
            ? monthKey
            : t('kpi.pmoFlow.scaleLeadTime.monthOption', {
                year: parsed.year,
                month: parsed.month,
              }),
      };
    }),
    {
      value: PMO_SCALE_ALL_MONTHS_KEY,
      label: t('kpi.pmoFlow.scaleLeadTime.allMonthsOption', { months: data?.months ?? 6 }),
    },
  ];

  // 軸は全体も含めた p90 の最大値。全体だけ別の軸で描くと 3 帯と長さを比べられなくなる
  const scopes: PmoScaleScopeStat[] = data ? [data.overall, ...data.bands] : [];
  const axisMax = scaleAxisMax(scopes);
  const scopeByKey = new Map(scopes.map((scope) => [scope.key, scope]));
  const openedScope = openedScopeKey === null ? null : (scopeByKey.get(openedScopeKey) ?? null);

  return (
    <Card className="animate-content-reveal">
      <CardHeader
        // 件数バッジ・月セレクトの下端を、左列の最下段（除外トグル）の下端に揃える
        // （既定の items-start だと右側だけタイトルの高さに張り付いて宙に浮く）
        className="items-end"
        title={t('kpi.pmoFlow.scaleLeadTime.title')}
        description={t('kpi.pmoFlow.scaleLeadTime.description')}
        action={
          data ? (
            <span className="flex items-center gap-2">
              <Badge variant="default" size="md">
                {t('kpi.pmoFlow.scaleLeadTime.totalCount', { count: data.totalCount })}
              </Badge>
              <Select
                options={monthOptions}
                value={activeMonth}
                onChange={(e) => {
                  onSelectMonth(e.target.value);
                }}
                size="sm"
                aria-label={t('kpi.pmoFlow.scaleLeadTime.monthLabel')}
              />
            </span>
          ) : undefined
        }
      >
        {/* トグルはヘッダー左列の最下段。件数バッジ・月セレクトと下端が揃い、
            カードの左端にも揃う。data がある限り出す（除外で 0 件になっても Raw へ戻せる） */}
        {data && (
          <div className="mt-3">
            <ExcludeModeToggle value={excludeMode} onChange={onSelectExcludeMode} />
          </div>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-40 animate-pulse rounded-lg bg-[var(--color-bg-elevated)]" />
        ) : data && data.totalCount > 0 ? (
          <>
            {/* 全体は 3 帯を比べる基準線なので、規模の並びの上に 1 枚だけ横長で置く */}
            <ScaleScopeBlock
              scope={data.overall}
              axisMax={axisMax}
              emphasis={true}
              onOpenDistribution={setOpenedScopeKey}
            />
            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
              {BAND_ORDER.map((key) => {
                const band = scopeByKey.get(key) ?? {
                  key,
                  count: 0,
                  averagePoint: null,
                  days: null,
                  distribution: [],
                };
                return (
                  <ScaleScopeBlock
                    key={key}
                    scope={band}
                    axisMax={axisMax}
                    onOpenDistribution={setOpenedScopeKey}
                  />
                );
              })}
            </div>
            {axisMax !== null && (
              <p className="mt-3 text-xs text-[var(--color-text-muted)]">
                {t('kpi.pmoFlow.scaleLeadTime.barLegend', { max: axisMax })}
              </p>
            )}
            {data.excludeMode !== 'none' && (
              <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                {t('kpi.pmoFlow.scaleLeadTime.exclude.activeNote', {
                  percentile: data.excludeMode === 'p75' ? 75 : 90,
                })}
              </p>
            )}
            <p className="mt-1 text-xs text-[var(--color-text-muted)]">
              {t('kpi.pmoFlow.scaleLeadTime.scopeNote')}
            </p>
            {data.excludedNoPointCount > 0 && (
              <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                {t('kpi.pmoFlow.scaleLeadTime.excludedNoPointNote', {
                  count: data.excludedNoPointCount,
                })}
              </p>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center rounded-lg border border-dashed border-[var(--color-border)] py-8">
            <p className="text-[var(--color-text-muted)]">{t('status.noData')}</p>
          </div>
        )}
      </CardContent>

      <ScaleLeadTimeDistributionModal
        open={openedScope !== null}
        onClose={() => {
          setOpenedScopeKey(null);
        }}
        scope={openedScope}
        month={activeMonth}
        excludeMode={data?.excludeMode ?? 'none'}
      />
    </Card>
  );
}
